/**
 * PaymentPlanPicker — shared count + cadence picker for any flow that
 * creates split-payment earnings (AddEarningsDialog, Clients re-up,
 * Clients new-client, ClientDetail re-up).
 *
 * Replaces the hardcoded `twoPaymentPlan` / `threePaymentPlan`
 * checkboxes that used to live inline in each dialog. Now trainers
 * pick any N from 1-12 and any cadence (monthly / biweekly / weekly).
 *
 * Why 1-12: covers ~every common plan trainers actually run (1, 2, 3,
 * 4, 6, 10, 12). Above 12 monthly = a multi-year financing arrangement
 * that probably wants Stripe Billing, not manual entry.
 *
 * The preview line ("$675 × 3 = $2,025") is rendered inline when N>1
 * so trainers see the math before confirming. Computed from
 * `totalAmount` + `equalSplit` flag — the same shape buildSplitPaymentRows
 * uses, so the preview can't drift from what actually gets inserted.
 */

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { PAYMENT_CADENCE_LABELS, type PaymentCadence } from "@/lib/split-payments";
import { cn } from "@/lib/utils";

interface PaymentPlanPickerProps {
  /** Current number of installments (1-12). 1 = single payment. */
  count: number;
  onCountChange: (next: number) => void;

  /** Current cadence. Ignored visually when count === 1 (no cadence to
      pick if there's only one payment). */
  cadence: PaymentCadence;
  onCadenceChange: (next: PaymentCadence) => void;

  /** Total amount for the package (re-up flows) OR per-payment amount
      (manual entry). Drives the preview line. */
  totalAmount: number;
  /** Whether totalAmount should be divided by count for the preview.
      Re-up flows pass true; manual entry passes false. Mirrors the
      flag on buildSplitPaymentRows() so preview ≡ actual. */
  equalSplit: boolean;

  /** Optional override on the available installment counts. Default
      1-12. Could be tier-gated later (Starter caps at 3, Pro at 6,
      etc) — for now everyone gets the full range. */
  maxCount?: number;

  className?: string;
}

// Currency formatter — kept inline rather than threading a locale prop
// since every callsite is USD today. Move to context if we go global.
const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});

export function PaymentPlanPicker({
  count,
  onCountChange,
  cadence,
  onCadenceChange,
  totalAmount,
  equalSplit,
  maxCount = 12,
  className,
}: PaymentPlanPickerProps) {
  const counts = Array.from({ length: maxCount }, (_, i) => i + 1);
  const amountPerPayment = equalSplit ? totalAmount / count : totalAmount;
  const grandTotal = equalSplit ? totalAmount : totalAmount * count;

  return (
    <div className={cn("space-y-2", className)}>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">Payment plan</Label>
          <Select
            value={String(count)}
            onValueChange={(v) => onCountChange(parseInt(v, 10))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {counts.map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n === 1 ? "1 payment" : `${n} payments`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {/* Cadence only meaningful when count > 1. Render but disable
            when count === 1 so the layout doesn't jump — trainer flips
            to 2+ and the field is immediately usable. */}
        <div className="space-y-1">
          <Label className="text-xs">Cadence</Label>
          <Select
            value={cadence}
            onValueChange={(v) => onCadenceChange(v as PaymentCadence)}
            disabled={count === 1}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(PAYMENT_CADENCE_LABELS) as PaymentCadence[]).map((c) => (
                <SelectItem key={c} value={c}>
                  {PAYMENT_CADENCE_LABELS[c]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Math preview. Only renders when there's a multi-payment plan
          to describe AND we have a real amount to divide; avoids
          flashing $0 × N during initial form state. */}
      {count > 1 && totalAmount > 0 && (
        <p className="text-xs text-muted-foreground tabular-nums">
          {usd.format(amountPerPayment)} × {count} = {usd.format(grandTotal)},{" "}
          <span className="lowercase">{PAYMENT_CADENCE_LABELS[cadence]}</span>
        </p>
      )}
    </div>
  );
}

export default PaymentPlanPicker;
